import React, { useEffect, useState } from 'react'
import Icon from '@components/atoms/Icon'
import { Swiper, SwiperSlide } from '@components/molecules/Slider'

interface AnnouncementMessage {
  copy: string
  url: string
  countdown_date?: string
  hide_days?: boolean
}

export interface AnnouncementData {
  messages: AnnouncementMessage[]
}

const PromotionalMessage: React.FC<AnnouncementMessage> = ({ copy, url, countdown_date, hide_days }) => {
  const [daysLeft, setDaysLeft] = useState<string>("");
  const [timeLeft, setTimeLeft] = useState<string>("");

  useEffect(() => {
    if (!countdown_date || countdown_date === "") return;

    const calculateTimeLeft = () => {
      const targetDate = new Date(countdown_date);
      const now = new Date();
      const mountainTime = new Date(now.toLocaleString("en-US", {timeZone: "America/Denver"}));
      const difference = targetDate.getTime() - mountainTime.getTime();

      if (difference <= 0) {
        setTimeLeft("");
        return;
      }

      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      let timeString = "";
      if (hours > 0) timeString += `${hours}h `;
      if (minutes > 0) timeString += `${minutes}m `;
      timeString += `${seconds}s`;

      let dayString = "";
      if (days > 0) dayString += `${days}d `;

      setDaysLeft(dayString.trim());
      setTimeLeft(timeString.trim());
    };

    const timer = setInterval(calculateTimeLeft, 1000);
    calculateTimeLeft();

    return () => clearInterval(timer);
  }, [countdown_date]);

  return (
      <div className="h-8 p-1.5 pl-5 flex justify-start items-center lg:p-1.5 lg:justify-center">
        <a href={url} className="block max-w-max util-sm text-secondary lg:text-center" aria-label={copy}>
          <span>{copy}</span>
        </a>
        {countdown_date && timeLeft && (
            <div className="text-sm util-sm text-secondary">&nbsp;{!hide_days && <span>{daysLeft} </span>}{timeLeft}</div>
        )}
      </div>
  );
};

const AnnouncementBar: React.FC<AnnouncementData> = ({ messages }) => {
  const [isVisible, setIsVisible] = useState(true)
  const [isFadingOut, setIsFadingOut] = useState(false)

  const handleClose = () => {
    setIsFadingOut(true)
    setTimeout(() => {
      setIsVisible(false)
      setIsFadingOut(false)

      const isMobile = window.innerWidth < 1024
      const customTop = isMobile ? 0 : 12
      const headerElement = document.querySelector('#shopify-section-header') as HTMLElement | null
      if (headerElement) headerElement.style.top = `${customTop}px`
    }, 500) // Duration of the fade-out animation
  }

  useEffect(() => {
    if (isVisible) {
      setIsFadingOut(false)
    }
  }, [isVisible])

  if (!isVisible) {
    return null // If not visible, return null to hide the component
  }

  return (
      <div className={`h-8 relative ${isFadingOut ? 'fade-out' : ''}`}>
        {messages.length === 1 ? (
            <PromotionalMessage copy={messages[0].copy} url={messages[0].url} countdown_date={messages[0].countdown_date} hide_days={messages[0].hide_days} />
        ) : (
            <Swiper
                direction="vertical"
                autoplay={{ delay: 3000, disableOnInteraction: false }}
                loop={true}
                className="!h-8"
            >
              {messages.map(({ copy, url, countdown_date, hide_days }: AnnouncementMessage) => (
                  <SwiperSlide key={copy}>
                    <PromotionalMessage copy={copy} url={url} countdown_date={countdown_date} hide_days={hide_days} />
                  </SwiperSlide>
              ))}
            </Swiper>
        )}

        <button
            data-close-announcement
            type="button"
            className="flex items-center justify-center w-8 h-8 absolute z-5 right-2.5 top-1/2 -translate-y-1/2"
            onClick={handleClose}
            aria-label="Close announcement"
        >
          <Icon name="close" className="text-secondary h-5 w-5" />
        </button>
      </div>
  )
}

export default AnnouncementBar
